import React, { useEffect, useMemo, useState } from "react";
import { Paper, Typography, TextField, Button, Grid } from "@mui/material";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { useAuth } from "../../../context/AuthContext";
import { createPlan, listPlans } from "../../../services/admin";
import { Link } from "react-router-dom";

export default function PlansPage() {
  useProtectedRoute();
  const { token, role } = useAuth();
  const can = role === 'SUPERADMIN';

  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create plan form state
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [priceMonthly, setPriceMonthly] = useState<number | string>("");
  const [currency, setCurrency] = useState("USD");
  const [maxUsers, setMaxUsers] = useState<number | string>("");
  const [features, setFeatures] = useState("");

  const canSubmit = useMemo(() => !!code && !!name && priceMonthly !== "" && !!currency, [code, name, priceMonthly, currency]);

  useEffect(() => {
    (async () => {
      if (!token || !can) return;
      setLoading(true); setError(null);
      try {
        const res = await listPlans(token);
        setPlans(res);
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Failed to load plans');
      } finally { setLoading(false); }
    })();
  }, [token, can]);

  if (!can) return <Paper elevation={0} className="p-6 rounded-2xl"><Typography>You do not have access.</Typography></Paper>;

  const onCreate = async () => {
    if (!token || !can || !canSubmit) return;
    setLoading(true); setError(null);
    try {
      const payload: any = {
        code: code.trim(),
        name: name.trim(),
        priceMonthly: Number(priceMonthly) || 0,
        currency: currency.trim() || 'USD',
      };
      if (maxUsers !== "" && maxUsers !== null) {
        const mu = Number(maxUsers);
        if (!Number.isNaN(mu)) payload.maxUsers = mu;
      }
      const feat = features.trim();
      if (feat) payload.features = feat.split(',').map(f => f.trim()).filter(Boolean);
      await createPlan(token, payload);
      // reload
      const res = await listPlans(token);
      setPlans(res);
      // reset form
      setCode(""); setName(""); setPriceMonthly(""); setCurrency("USD"); setMaxUsers(""); setFeatures("");
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create plan');
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="flex items-center justify-between mb-3">
        <Typography variant="h5" fontWeight={900}>SaaS Admin – Plans</Typography>
        <div className="flex gap-2">
          <Button component={Link} to="/control" size="small" variant="outlined">Tenants</Button>
          <Button component={Link} to="/control/subscriptions" size="small" variant="outlined">Subscriptions</Button>
        </div>
      </div>
      {error && <Typography color="error" className="mb-2">{error}</Typography>}

      <Paper elevation={0} className="p-0 overflow-x-auto rounded-2xl mb-4" style={{ border: '1px solid var(--mui-palette-divider)' }}>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="py-2 px-3 text-left text-gray-700">Code</th>
              <th className="py-2 px-3 text-left text-gray-700">Name</th>
              <th className="py-2 px-3 text-left text-gray-700">Price (Monthly)</th>
              <th className="py-2 px-3 text-left text-gray-700">Currency</th>
              <th className="py-2 px-3 text-left text-gray-700">Max Users</th>
              <th className="py-2 px-3 text-left text-gray-700">Features</th>
            </tr>
          </thead>
          <tbody>
            {!plans.length && !loading && (
              <tr><td colSpan={5} className="text-center text-gray-400 py-6">No plans</td></tr>
            )}
            {plans.map((p: any) => (
              <tr key={p._id} className="border-b border-gray-200">
                <td className="py-2 px-3">{p.code}</td>
                <td className="py-2 px-3">{p.name}</td>
                <td className="py-2 px-3">{Number(p.priceMonthly).toFixed(2)}</td>
                <td className="py-2 px-3">{p.currency}</td>
                <td className="py-2 px-3">{typeof p.maxUsers === 'number' && p.maxUsers > 0 ? p.maxUsers : 'Unlimited'}</td>
                <td className="py-2 px-3">{(p.features || []).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="text-center py-6">Loading…</div>}
      </Paper>

      <Paper elevation={0} className="p-4 rounded-2xl" style={{ border: '1px solid var(--mui-palette-divider)' }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Create Plan</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}><TextField fullWidth size="small" label="Code" value={code} onChange={(e)=>setCode(e.target.value)} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Name" value={name} onChange={(e)=>setName(e.target.value)} /></Grid>
          <Grid item xs={6} md={2}><TextField fullWidth size="small" label="Price / mo" type="number" value={priceMonthly} onChange={(e)=>setPriceMonthly(e.target.value)} /></Grid>
          <Grid item xs={6} md={3}><TextField fullWidth size="small" label="Currency" value={currency} onChange={(e)=>setCurrency(e.target.value)} /></Grid>
          <Grid item xs={6} md={3}><TextField fullWidth size="small" label="Max Users (blank = unlimited)" type="number" value={maxUsers} onChange={(e)=>setMaxUsers(e.target.value)} /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" label="Features (comma separated)" value={features} onChange={(e)=>setFeatures(e.target.value)} /></Grid>
          <Grid item xs={12}><Button disabled={!canSubmit || loading} variant="contained" onClick={onCreate}>Create</Button></Grid>
        </Grid>
      </Paper>
    </div>
  );
}
